'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { MONO_FONT } from '@/theme';

// Tarjeta de pieza del blog / Actualidad: portada, meta, título, resumen y autor.
// La usa ContentFeed en /blog y /actualidad (mismo render para artículos y notas).

/* ─────────────────────────────────────────────
   POST CARD
───────────────────────────────────────────── */

function PostCover({ image, alt, topic }) {
  if (image) {
    return (
      <Box
        component="img"
        src={image}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    );
  }
  // Fallback para artículos sin imagen.
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'grey.950',
        display: 'grid',
        placeItems: 'center',
        backgroundImage:
          'linear-gradient(135deg, rgba(0,159,139,0.18), transparent 70%), repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 32px), repeating-linear-gradient(90deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 32px)',
      }}
    >
      <Box
        sx={{
          fontFamily: MONO_FONT,
          fontSize: '11px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'primary.glow',
          opacity: 0.8,
        }}
      >
        {topic}
      </Box>
    </Box>
  );
}

export default function PostCard({ post }) {
  return (
    <Box
      component={Link}
      href={post.href}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'transform 0.18s ease',
        '&:hover': { transform: 'translateY(-2px)' },
        '&:hover .post-title': { color: 'text.accent' },
      }}
    >
      {/* Cover */}
      <Box
        sx={{
          aspectRatio: '16 / 9',
          borderRadius: '4px',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'grey.200',
          position: 'relative',
        }}
      >
        <PostCover image={post.image} alt={post.imageAlt} topic={post.coverLabel} />
      </Box>

      {/* Meta */}
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          alignItems: 'center',
          fontFamily: MONO_FONT,
          fontSize: '10.5px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.muted',
        }}
      >
        <Box component="span" sx={{ color: 'text.accent', fontWeight: 600 }}>
          {post.topic}
        </Box>
        <Box component="span" sx={{ color: 'grey.300' }}>
          /
        </Box>
        <span>{post.date}</span>
        <Box component="span" sx={{ color: 'grey.300' }}>
          /
        </Box>
        <span>{post.readTime}</span>
      </Box>

      {/* Title */}
      <Typography
        className="post-title"
        component="h3"
        sx={{
          fontSize: '17px',
          fontWeight: 600,
          letterSpacing: '-0.014em',
          color: 'text.primary',
          lineHeight: 1.25,
          transition: 'color 0.15s ease',
        }}
      >
        {post.title}
      </Typography>

      {/* Excerpt */}
      <Typography
        sx={{ fontSize: '14px', lineHeight: 1.55, color: 'grey.600' }}
      >
        {post.card}
      </Typography>

      {/* Author */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          mt: 'auto',
          pt: 1.5,
        }}
      >
        {post.avatar && (
          <Box
            component="img"
            src={post.avatar}
            alt={post.authorName}
            sx={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
        )}
        <Box
          component="span"
          sx={{
            fontSize: '12.5px',
            fontWeight: 500,
            color: 'grey.700',
            letterSpacing: '-0.01em',
          }}
        >
          {post.authorName}
        </Box>
        <Box
          component="span"
          sx={{ color: 'grey.300', lineHeight: 1, fontSize: '12px' }}
        >
          ·
        </Box>
        <Box
          component="span"
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '10px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.muted',
          }}
        >
          {post.authorSub}
        </Box>
      </Box>
    </Box>
  );
}
